import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Boxes,
  Package,
  Plus,
  Search,
  ArrowLeft,
  ShieldCheck,
  Image as ImageIcon,
} from "lucide-react";
import Swal from "sweetalert2";
import api from "../Api/Api";

const AddStock = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const selectedProductFromNavigation =
    location.state?.product || null;

  const [products, setProducts] = useState([]);
  const [stockToAdd, setStockToAdd] = useState({});
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const role = localStorage.getItem("role");

  useEffect(() => {
    const fetchProducts = async () => {
      if (role !== "admin") {
        setLoading(false);

        await Swal.fire({
          icon: "error",
          title: "Unauthorized",
          text: "You are not authorized to view this page.",
          confirmButtonColor: "#2563EB",
        });

        navigate("/");

        return;
      }

      try {
        const response = await api.get("/get_products/");

        const fetchedProducts = Array.isArray(
          response.data?.products
        )
          ? response.data.products
          : [];

        setProducts(fetchedProducts);

        // If the admin clicked Add Stock from a specific product,
        // move that product to the top.
        if (selectedProductFromNavigation) {
          setProducts((prev) => {
            const selected = prev.find(
              (item) =>
                item.id === selectedProductFromNavigation.id
            );

            if (!selected) return prev;

            return [
              selected,
              ...prev.filter(
                (item) => item.id !== selected.id
              ),
            ];
          });
        }
      } catch (error) {
        console.error("Error fetching products:", error);

        Swal.fire({
          icon: "error",
          title: "Unable to Load Products",
          text:
            error.response?.data?.message ||
            error.response?.data?.detail ||
            "Failed to load products. Please try again.",
          confirmButtonColor: "#2563EB",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [
    role,
    navigate,
    selectedProductFromNavigation,
  ]);

  const handleInputChange = (productId, value) => {
    setStockToAdd((prev) => ({
      ...prev,
      [productId]: value,
    }));
  };

  const handleAddStock = async (product) => {
    const rawValue = stockToAdd[product.id];

    const additionalStock = Number(rawValue);

    if (
      rawValue === undefined ||
      rawValue === "" ||
      !Number.isInteger(additionalStock) ||
      additionalStock <= 0
    ) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Stock Quantity",
        text: "Please enter a whole number greater than zero.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    const result = await Swal.fire({
      icon: "question",
      title: "Update Stock?",
      html: `
        <p style="color:#64748b">
          Add <strong>${additionalStock}</strong> units to
          <strong>${product.name}</strong>?
        </p>
      `,
      showCancelButton: true,
      confirmButtonText: "Yes, Add Stock",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#2563EB",
      cancelButtonColor: "#64748B",
    });

    if (!result.isConfirmed) return;

    setUpdatingId(product.id);

    try {
      const response = await api.get(
        `/add_stock/${product.id}/${additionalStock}`
      );

      const data = response.data;

      const newStock =
        data.new_stock ??
        Number(product.stock || 0) + additionalStock;

      setProducts((prev) =>
        prev.map((item) =>
          item.id === product.id
            ? {
                ...item,
                stock: newStock,
              }
            : item
        )
      );

      setStockToAdd((prev) => ({
        ...prev,
        [product.id]: "",
      }));

      Swal.fire({
        icon: "success",
        title: "Stock Updated",
        text: `${product.name} now has ${newStock} units in stock.`,
        confirmButtonColor: "#2563EB",
      });
    } catch (error) {
      console.error("Error updating stock:", error);

      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          error.response?.data?.message ||
          error.response?.data?.detail ||
          "There was an error updating the stock.",
        confirmButtonColor: "#2563EB",
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredProducts = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    if (!search) return products;

    return products.filter((product) => {
      const name = product.name?.toLowerCase() || "";
      const category =
        product.category?.toLowerCase() || "";

      return (
        name.includes(search) ||
        category.includes(search)
      );
    });
  }, [products, searchTerm]);

  const totalStock = useMemo(() => {
    return products.reduce(
      (total, product) =>
        total + Number(product.stock || 0),
      0
    );
  }, [products]);

  const lowStockCount = useMemo(() => {
    return products.filter(
      (product) =>
        Number(product.stock || 0) <= 5
    ).length;
  }, [products]);

  if (role !== "admin" && !loading) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Header */}
      <section className="relative bg-slate-950 text-white pt-20 pb-28 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[450px] h-[450px] bg-blue-600/20 rounded-full blur-[140px]" />

        <div className="absolute -bottom-40 right-0 w-[450px] h-[450px] bg-cyan-400/10 rounded-full blur-[130px]" />

        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto">
          <button
            onClick={() => navigate("/products")}
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition mb-8 text-sm font-semibold"
          >
            <ArrowLeft size={17} />
            Back to Products
          </button>

          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-400/20 bg-blue-500/10 text-blue-300 text-sm font-semibold mb-6">
                <ShieldCheck size={15} />
                Inventory Management
              </div>

              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
                Add Product Stock
              </h1>

              <p className="text-slate-300 text-lg leading-relaxed">
                Update inventory quantities and keep
                Nexindi Shop stock levels accurate.
              </p>
            </div>

            {!loading && (
              <div className="inline-flex items-center gap-3 bg-white/10 border border-white/10 backdrop-blur-md rounded-2xl px-5 py-4">
                <Boxes
                  size={20}
                  className="text-blue-300"
                />

                <div>
                  <p className="text-2xl font-bold">
                    {totalStock.toLocaleString()}
                  </p>

                  <p className="text-xs text-slate-400">
                    Units in inventory
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Main */}
      <main className="relative -mt-14 pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Summary */}
          {!loading && (
            <div className="grid md:grid-cols-3 gap-5 mb-8">
              <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xl shadow-slate-900/5">
                <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-5">
                  <Package size={21} />
                </div>

                <p className="text-sm text-slate-500 mb-2">
                  Products
                </p>

                <p className="text-3xl font-bold">
                  {products.length}
                </p>
              </div>

              <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xl shadow-slate-900/5">
                <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-5">
                  <Boxes size={21} />
                </div>

                <p className="text-sm text-slate-500 mb-2">
                  Total Stock
                </p>

                <p className="text-3xl font-bold">
                  {totalStock.toLocaleString()}
                </p>
              </div>

              <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xl shadow-slate-900/5">
                <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mb-5">
                  <Package size={21} />
                </div>

                <p className="text-sm text-slate-500 mb-2">
                  Low Stock Products
                </p>

                <p className="text-3xl font-bold">
                  {lowStockCount}
                </p>
              </div>
            </div>
          )}

          {/* Inventory Panel */}
          <section className="bg-white border border-slate-200 rounded-3xl shadow-xl shadow-slate-900/5 overflow-hidden">
            {/* Toolbar */}
            <div className="p-6 md:p-7 border-b border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-5">
              <div>
                <h2 className="text-xl font-bold">
                  Inventory
                </h2>

                <p className="text-sm text-slate-500 mt-1">
                  Select a product and enter the quantity to add.
                </p>
              </div>

              <div className="relative w-full md:max-w-sm">
                <Search
                  size={18}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                />

                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) =>
                    setSearchTerm(e.target.value)
                  }
                  placeholder="Search products..."
                  className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                />
              </div>
            </div>

            {/* Loading */}
            {loading && (
              <div className="p-7 space-y-4">
                {[1, 2, 3, 4, 5].map((item) => (
                  <div
                    key={item}
                    className="h-24 rounded-2xl bg-slate-100 animate-pulse"
                  />
                ))}
              </div>
            )}

            {/* Empty */}
            {!loading &&
              filteredProducts.length === 0 && (
                <div className="py-20 px-6 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-6">
                    <Package size={28} />
                  </div>

                  <h3 className="text-2xl font-bold mb-3">
                    No products found
                  </h3>

                  <p className="text-slate-500 max-w-md mx-auto">
                    There are no products matching your
                    current search.
                  </p>
                </div>
              )}

            {/* Desktop Table */}
            {!loading &&
              filteredProducts.length > 0 && (
                <>
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="px-7 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                            Product
                          </th>

                          <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                            Current Stock
                          </th>

                          <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                            Add Quantity
                          </th>

                          <th className="px-7 py-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">
                            Action
                          </th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-slate-100">
                        {filteredProducts.map(
                          (product) => (
                            <tr
                              key={product.id}
                              className={`transition ${
                                selectedProductFromNavigation?.id ===
                                product.id
                                  ? "bg-blue-50/50"
                                  : "hover:bg-slate-50"
                              }`}
                            >
                              <td className="px-7 py-5">
                                <div className="flex items-center gap-4">
                                  <div className="w-14 h-14 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex-shrink-0">
                                    {product.image ? (
                                      <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center">
                                        <ImageIcon
                                          size={20}
                                          className="text-slate-300"
                                        />
                                      </div>
                                    )}
                                  </div>

                                  <div>
                                    <p className="font-semibold text-slate-900">
                                      {product.name}
                                    </p>

                                    <p className="text-xs text-slate-400 mt-1">
                                      {product.category ||
                                        "Others"}
                                    </p>
                                  </div>
                                </div>
                              </td>

                              <td className="px-5 py-5">
                                <span className="font-bold text-slate-900">
                                  {Number(
                                    product.stock || 0
                                  ).toLocaleString()}
                                </span>
                              </td>

                              <td className="px-5 py-5">
                                <div className="relative w-32">
                                  <Plus
                                    size={15}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                  />

                                  <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={
                                      stockToAdd[
                                        product.id
                                      ] || ""
                                    }
                                    onChange={(e) =>
                                      handleInputChange(
                                        product.id,
                                        e.target.value
                                      )
                                    }
                                    placeholder="0"
                                    className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                                  />
                                </div>
                              </td>

                              <td className="px-7 py-5 text-right">
                                <button
                                  onClick={() =>
                                    handleAddStock(
                                      product
                                    )
                                  }
                                  disabled={
                                    updatingId ===
                                    product.id
                                  }
                                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                  {updatingId ===
                                  product.id ? (
                                    <>
                                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                      Updating...
                                    </>
                                  ) : (
                                    <>
                                      <Plus size={15} />
                                      Add Stock
                                    </>
                                  )}
                                </button>
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile */}
                  <div className="md:hidden divide-y divide-slate-100">
                    {filteredProducts.map(
                      (product) => (
                        <div
                          key={product.id}
                          className="p-5"
                        >
                          <div className="flex gap-4">
                            <div className="w-20 h-20 rounded-2xl bg-slate-100 border border-slate-200 overflow-hidden flex-shrink-0">
                              {product.image ? (
                                <img
                                  src={product.image}
                                  alt={product.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <ImageIcon
                                    size={23}
                                    className="text-slate-300"
                                  />
                                </div>
                              )}
                            </div>

                            <div>
                              <h3 className="font-bold text-slate-900">
                                {product.name}
                              </h3>

                              <p className="text-sm text-slate-500 mt-1">
                                {product.category ||
                                  "Others"}
                              </p>

                              <p className="text-sm mt-3">
                                Current stock:{" "}
                                <strong>
                                  {Number(
                                    product.stock || 0
                                  ).toLocaleString()}
                                </strong>
                              </p>
                            </div>
                          </div>

                          <div className="mt-5">
                            <label className="text-xs font-semibold text-slate-500">
                              Quantity to Add
                            </label>

                            <input
                              type="number"
                              min="1"
                              step="1"
                              value={
                                stockToAdd[
                                  product.id
                                ] || ""
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  product.id,
                                  e.target.value
                                )
                              }
                              placeholder="Enter quantity"
                              className="w-full mt-2 px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                            />
                          </div>

                          <button
                            onClick={() =>
                              handleAddStock(product)
                            }
                            disabled={
                              updatingId === product.id
                            }
                            className="w-full mt-4 flex items-center justify-center gap-2 bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition disabled:opacity-60"
                          >
                            {updatingId ===
                            product.id ? (
                              <>
                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Updating...
                              </>
                            ) : (
                              <>
                                <Plus size={16} />
                                Add Stock
                              </>
                            )}
                          </button>
                        </div>
                      )
                    )}
                  </div>
                </>
              )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default AddStock;